import React from 'react';
import { Navigate } from 'react-router-dom';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { LogOutComponent } from '../../components';

/**
 * RequireRole Higher-Order Component
 * 
 * Protects routes/components by requiring specific roles.
 * More flexible than RequiredAuth as it supports multiple role configurations.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if access is granted
 * @param {string[]} [props.allowedRoles] - Array of roles that can access this component
 * @param {string} [props.minimumRole] - Minimum role required (uses role hierarchy)
 * @param {boolean} [props.showAccessDenied] - Show access denied message instead of redirecting
 * @param {string} [props.redirectTo] - Where to redirect if access is denied (default: /login)
 * @param {React.ReactNode} [props.fallback] - Custom fallback component
 */
const RequireRole = ({
  children,
  allowedRoles = null,
  minimumRole = null,
  showAccessDenied = true,
  redirectTo = '/login',
  fallback = null,
}) => {
  const {
    userRole,
    hasAnyRole,
    meetsMinimumRole,
    isValidRole,
  } = useRoleAccess();

  // Check if user has valid role
  if (!userRole || !isValidRole) {
    if (showAccessDenied) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh',
            fontSize: '16px',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <h2>Invalid or Missing Role</h2>
          <p>Your account doesn't have a valid role assigned.</p>
          <div style={{ marginTop: '20px' }}>
            <LogOutComponent />
          </div>
        </div>
      );
    }
    return <Navigate to={redirectTo} replace />;
  }

  // If no restrictions specified, allow access
  if (!allowedRoles && !minimumRole) {
    return children;
  }

  // Check against allowed roles
  let hasAccess = false;

  if (allowedRoles && Array.isArray(allowedRoles)) {
    hasAccess = hasAnyRole(allowedRoles);
  } else if (minimumRole) {
    hasAccess = meetsMinimumRole(minimumRole);
  }

  // Access denied
  if (!hasAccess) {
    // Show custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Show access denied message
    if (showAccessDenied) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh',
            fontSize: '16px',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this section.</p>
          <p style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Your current role: <strong>{userRole}</strong>
          </p>
          {allowedRoles && (
            <p style={{ color: '#666', fontSize: '0.875rem' }}>
              Required roles: <strong>{allowedRoles.join(', ')}</strong>
            </p>
          )}
          <div style={{ marginTop: '20px' }}>
            <LogOutComponent />
          </div>
        </div>
      );
    }

    // Redirect to specified route
    return <Navigate to={redirectTo} replace />;
  }

  // Access granted
  return children;
};

export { RequireRole };
